import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import {
  ChartBarLine,
  ContactsLine,
  ExitDoorLine,
  GridLine,
  InboxLine,
  ListCheckLine,
  Message3Line,
  More2Line,
  PluginLine,
  Settings3Line,
  Transfer4Line,
} from "@mingcute/react";
import { useLocation, useNavigate } from "react-router-dom";

import { updateProfileMe } from "../../../../api/profile/updateProfileMe";
import { useAuth } from "../../../../app/providers/AuthProvider";
import useAccountSettingsStore from "../../../../features/settings/store/useAccountSettingsStore";
import use123zapStore from "../../../../stores/123zapStore";
import {
  AccountMenuHeader,
  ACCOUNT_MENU_DIVIDER_SX,
  ACCOUNT_MENU_ITEM_SX,
  ACCOUNT_MENU_LIST_SX,
  ACCOUNT_MENU_PAPER_SX,
  ACCOUNT_MENU_WIDTH,
  UserProfileDialog,
  resolveAccountMenuIdentity,
} from "../../../../shared/components/product";
import { ActionMenu } from "../../../../shared/components/ui";
import {
  canAccessPermission,
  isFeatureAllowed,
} from "../../../../shared/lib/access/featureAccess";
import { getHasMultipleAccounts } from "../../../../shared/lib/auth/accountContext";
import {
  buildUserAvatarUrl,
  subscribeToAvatarChanges,
} from "../../../../shared/lib/userAvatar";
import { SHOW_ATTENDANCE_NAVIGATION } from "./navigationFlags";

const SHOW_SHELL_NOTIFICATIONS = false;
const SIDEBAR_COLOR_TRANSITION =
  "background-color 180ms ease, border-color 180ms ease, color 180ms ease";
export const APP_SHELL = {
  sidebarExpandedWidth: 264,
  sidebarCompactWidth: 84,
  topbarHeight: 72,
  mobileDrawerWidth: 320,
  mobileHeaderHeight: 64,
  desktopBreakpoint: "md",
};

const navSections = [
  {
    title: "Principal",
    items: [
      { label: "Relatórios", href: "/painel", icon: ChartBarLine },
      {
        label: "Atendimentos",
        href: "/atendimentos",
        icon: Message3Line,
        hidden: !SHOW_ATTENDANCE_NAVIGATION,
        access: { module: "crm.attendance" },
      },
      {
        label: "Canais",
        href: "/canais",
        icon: InboxLine,
        access: { action: "use", module: "channels" },
      },
      {
        label: "Listagens",
        href: "/listagens",
        icon: ListCheckLine,
        access: { action: "use", module: "audiences" },
      },
      {
        label: "Contatos",
        href: "/contatos",
        icon: ContactsLine,
        access: { action: "use", module: "audiences" },
      },
      {
        label: "Modelos",
        href: "/modelos",
        icon: Message3Line,
        access: { action: "use", module: "message_templates" },
      },
      {
        label: "Campanhas",
        href: "/campanhas",
        icon: ChartBarLine,
        access: {
          action: "view",
          module: "campaigns",
        },
      },
    ],
  },
  {
    title: "Gestão",
    items: [
      {
        label: "Conta",
        href: "/ajustes/empresa",
        activePrefix: "/ajustes/empresa",
        icon: Settings3Line,
        access: { action: "use", module: "account_settings" },
      },
      {
        label: "Funcionalidades",
        href: "/ajustes/funcionalidades",
        icon: GridLine,
        access: { action: "use", module: "account_features" },
      },
      {
        label: "Integrações",
        href: "/ajustes/integracoes",
        icon: PluginLine,
        access: { action: "use", module: "integrations" },
      },
    ],
  },
];

function isNavItemActive(pathname, item) {
  const activePath = item.activePrefix || item.href;
  return pathname === activePath || pathname.startsWith(`${activePath}/`);
}

function isNavItemAllowed(item, user, featureAccess) {
  if (item?.hidden) {
    return false;
  }

  if (item?.access?.module && !isFeatureAllowed(featureAccess, item.access.module)) {
    return false;
  }

  if (item?.access?.action) {
    return canAccessPermission(user, item.access.module, item.access.action);
  }

  return true;
}

export function getPrivatePageTitle(pathname) {
  const decoded = decodeURIComponent(pathname);
  const allItems = navSections.flatMap((section) => section.items);
  const match = allItems
    .filter((item) => isNavItemActive(decoded, item))
    .sort((first, second) => {
      const firstPath = first.activePrefix || first.href;
      const secondPath = second.activePrefix || second.href;
      return secondPath.length - firstPath.length;
    })[0];

  return match?.label || "Título da página";
}

export function getPrivateShellColors(theme, sidebarDark) {
  const custom = theme.palette.custom;

  if (sidebarDark) {
    return {
      activeBg: theme.palette.action.selected,
      activeText: custom.purple[200],
      background: custom.gray[900],
      border: custom.gray[700],
      brand: custom.purple[500],
      icon: custom.gray[500],
      muted: custom.gray[500],
      navText: custom.gray[300],
      scrim: alpha(theme.palette.common.black, 0.42),
      surface: custom.gray[800],
      text: custom.gray[50],
    };
  }

  return {
    activeBg: custom.purple[50],
    activeText: theme.palette.primary.main,
    background: theme.palette.background.default,
    border: theme.palette.border.default,
    brand: theme.palette.primary.main,
    icon: custom.gray[300],
    muted: theme.palette.text.secondary,
    navText: theme.palette.text.secondary,
    scrim: alpha(theme.palette.custom.gray[900], 0.36),
    surface: theme.palette.background.paper,
    text: theme.palette.text.primary,
  };
}

function NavItem({ compact, colors, item, active, labelVisible, onClick }) {
  const Icon = item.icon;
  const showLabel = !compact && labelVisible;
  const content = (
    <ButtonBase
      type="button"
      onClick={onClick}
      aria-label={item.label}
      sx={{
        alignItems: "center",
        bgcolor: active ? colors.activeBg : "transparent",
        borderRadius: 2,
        color: active ? colors.activeText : colors.navText,
        display: "flex",
        gap: showLabel ? 1.5 : 0,
        height: 40,
        justifyContent: "flex-start",
        px: 2,
        transition: [
          SIDEBAR_COLOR_TRANSITION,
          "gap 260ms cubic-bezier(0.4, 0, 0.2, 1)",
          "padding 320ms cubic-bezier(0.4, 0, 0.2, 1)",
          "width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        ].join(", "),
        maxWidth: 232,
        width: "100%",
        "@media (max-height: 760px)": {
          height: 36,
        },
        "&:hover": {
          bgcolor: active ? colors.activeBg : "action.hover",
        },
        "&:focus-visible": {
          outline: (theme) => `2px solid ${theme.palette.border.focus}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        aria-hidden
        component="span"
        sx={{
          alignItems: "center",
          display: "flex",
          flexShrink: 0,
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        <Icon aria-hidden size={18} color="currentColor" />
      </Box>
      <Typography
        component="span"
        sx={{
          color: "currentColor",
          fontSize: 13,
          fontWeight: active ? 700 : 500,
          lineHeight: "18px",
          maxWidth: showLabel ? 160 : 0,
          minWidth: 0,
          opacity: showLabel ? 1 : 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          transition: "opacity 140ms ease, max-width 260ms cubic-bezier(0.4, 0, 0.2, 1)",
          whiteSpace: "nowrap",
          "@media (max-height: 760px)": {
            fontSize: 12,
            lineHeight: "16px",
          },
        }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  );

  if (!compact) return content;

  return (
    <Tooltip title={item.label} placement="right">
      {content}
    </Tooltip>
  );
}

function Navigation({
  compact = false,
  colors,
  labelVisible = !compact,
  mobile = false,
  onItemClick,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const accountProfile = useAccountSettingsStore(
    (state) => state.accountState.profile,
  );
  const featureAccess = use123zapStore((state) => state.featureAccess);
  const featureAccessAccountId = use123zapStore((state) => state.featureAccessAccountId);
  const featureAccessLoaded = use123zapStore((state) => state.featureAccessLoaded);
  const featureAccessLoading = use123zapStore((state) => state.featureAccessLoading);
  const featureAccessReady = Boolean(
    !user?.accountId ||
    (
      featureAccessLoaded &&
      !featureAccessLoading &&
      featureAccess &&
      featureAccessAccountId === user.accountId
    ),
  );
  const effectiveFeatureAccess = featureAccessReady ? featureAccess : null;
  const decoded = decodeURIComponent(pathname);
  const rail = compact;
  const accountProfileId =
    accountProfile?.account_id ||
    accountProfile?.accountId ||
    accountProfile?.id ||
    "";
  const accountPermissions =
    accountProfileId && accountProfileId === user?.accountId
      ? accountProfile?.permissions
      : null;
  const permissionUser = React.useMemo(
    () => (
      accountPermissions
        ? { ...user, permissions: accountPermissions }
        : user
    ),
    [accountPermissions, user],
  );
  const visibleSections = React.useMemo(
    () =>
      featureAccessReady
        ? navSections
          .map((section) => ({
            ...section,
            items: section.items.filter((item) => isNavItemAllowed(item, permissionUser, effectiveFeatureAccess)),
          }))
          .filter((section) => section.items.length > 0)
        : [],
    [effectiveFeatureAccess, featureAccessReady, permissionUser],
  );

  function goTo(item) {
    navigate(item.href);
    onItemClick?.();
  }

  return (
    <Stack
      component="nav"
      spacing={mobile ? 1.25 : 3}
      sx={{
        alignItems: "stretch",
        flex: "1 1 auto",
        minHeight: 0,
        overflowX: "hidden",
        overflowY: "auto",
        overscrollBehavior: "contain",
        px: 2,
        py: mobile ? 1.5 : 2,
        scrollbarColor: rail ? "transparent transparent" : `${colors.border} transparent`,
        scrollbarWidth: rail ? "none" : "thin",
        transition: "gap 260ms cubic-bezier(0.4, 0, 0.2, 1), padding 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        "&::-webkit-scrollbar": {
          width: rail ? 0 : 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.border,
          borderRadius: 999,
          transition: "background-color 180ms ease",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "@media (max-height: 760px)": {
          py: mobile ? 1 : 1.25,
        },
      }}
    >
      {visibleSections.map((section) => {
        const showSectionTitle = section.title !== "Principal";

        return (
          <Stack
            key={section.title}
            spacing={mobile ? 0.75 : 1}
            sx={{
              alignItems: "stretch",
              transition: "align-items 180ms ease",
            }}
          >
            {showSectionTitle ? (
              <Typography
                sx={{
                  color: colors.navText,
                  fontSize: 11,
                  fontWeight: 600,
                  height: 17,
                  lineHeight: "17px",
                  opacity: !rail && labelVisible ? 1 : 0,
                  overflow: "hidden",
                  px: 1,
                  transition: "color 180ms ease, opacity 120ms ease",
                }}
              >
                {section.title}
              </Typography>
            ) : null}

            <Stack
              spacing={mobile ? 0.75 : 1}
              sx={{
                alignItems: "stretch",
                transition: "align-items 180ms ease",
              }}
            >
              {section.items.map((item) => {
                const active = isNavItemActive(decoded, item);

                return (
                  <NavItem
                    key={item.href}
                    compact={rail}
                    colors={colors}
                    item={item}
                    active={active}
                    labelVisible={labelVisible}
                    onClick={() => goTo(item)}
                  />
                );
              })}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

function UserFooter({
  compact = false,
  colors,
  labelVisible = !compact,
  mobile = false,
  onLogout,
  onSwitchAccount,
}) {
  const { updateAuthenticatedUser, user } = useAuth();
  const accountState = useAccountSettingsStore((state) => state.accountState);
  const [profileDialogOpen, setProfileDialogOpen] = React.useState(false);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [avatarVersion, setAvatarVersion] = React.useState(0);
  const initial = (user?.name || user?.email || "Z").trim().charAt(0).toUpperCase();
  const avatarUrl = buildUserAvatarUrl(user);
  const accountIdentity = resolveAccountMenuIdentity({ accountState, user });
  const accountName = accountIdentity.name;
  const accountLogoUrl = accountIdentity.logoUrl;
  const accountInitial = accountIdentity.initial;
  const title = accountName;
  const meta = user?.email || "Sessão ativa";
  const rail = compact;
  const expanded = !compact;
  const showLabel = expanded && labelVisible;
  const canSwitchAccount =
    Number(user?.accountsCount || 0) > 1 ||
    getHasMultipleAccounts(user?.email);

  React.useEffect(() => subscribeToAvatarChanges(() => {
    setAvatarVersion((current) => current + 1);
  }), []);

  const handleProfileSave = React.useCallback(async (profileData) => {
    setProfileSaving(true);

    try {
      const response = await updateProfileMe({
        name: String(profileData?.name || "").trim(),
        nickname: String(profileData?.nickname || "").trim(),
      });
      const updatedUser = response?.user || {};

      updateAuthenticatedUser({
        ...user,
        name: updatedUser.name || updatedUser.display_name || profileData?.name || user?.name,
        nickname: updatedUser.nickname || profileData?.nickname || "",
      });
    } finally {
      setProfileSaving(false);
    }
  }, [updateAuthenticatedUser, user]);

  const avatar = (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: colors.activeBg,
        border: `1px solid ${colors.border}`,
        borderRadius: "50%",
        color: colors.activeText,
        display: "flex",
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 800,
        height: 28,
        justifyContent: "center",
        overflow: "hidden",
        transition: SIDEBAR_COLOR_TRANSITION,
        width: 28,
      }}
    >
      {accountLogoUrl ? (
        <Box
          component="img"
          src={accountLogoUrl}
          alt=""
          sx={{ height: "100%", objectFit: "cover", width: "100%" }}
        />
      ) : (
        accountInitial
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        pb: rail ? 3.5 : 3,
        px: rail ? 1.5 : 3,
        pt: 2,
        flex: "0 0 auto",
        transition: "padding 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        "@media (max-height: 760px)": {
          pb: rail ? 2.375 : 2,
          px: rail ? 1.25 : 2,
          pt: 1.5,
        },
      }}
    >
      <Box
        sx={{
          height: expanded ? 21 : 0,
          opacity: expanded && labelVisible ? 1 : 0,
          overflow: "hidden",
          transition: "height 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 140ms ease",
        }}
      >
        <Divider sx={{ borderColor: colors.border, transition: "border-color 180ms ease" }} />
      </Box>
      <ActionMenu
        ariaLabel="Abrir opções da conta"
        menuItemSx={ACCOUNT_MENU_ITEM_SX}
        menuListSx={ACCOUNT_MENU_LIST_SX}
        menuHeader={({ closeMenu }) => (
          <AccountMenuHeader
            avatarUrl={avatarUrl}
            avatarVersion={avatarVersion}
            email={user?.email}
            initial={initial}
            onEditProfile={() => {
              closeMenu();
              setProfileDialogOpen(true);
            }}
            productName={accountName}
            userName={user?.name || user?.username}
          />
        )}
        menuPaperSx={ACCOUNT_MENU_PAPER_SX}
        minWidth={ACCOUNT_MENU_WIDTH}
        placement="top-end"
        actions={[
          {
            id: "switch-account",
            icon: <Transfer4Line aria-hidden size={24} color="currentColor" />,
            label: "Trocar conta",
            hidden: !canSwitchAccount,
            onClick: onSwitchAccount,
          },
          {
            id: "account-actions-divider",
            type: "divider",
            hidden: !canSwitchAccount,
            sx: ACCOUNT_MENU_DIVIDER_SX,
          },
          {
            id: "logout",
            icon: <ExitDoorLine aria-hidden size={24} color="currentColor" />,
            label: "Sair",
            onClick: onLogout,
          },
        ]}
        renderTrigger={({ buttonProps }) => {
          const trigger = (
            <ButtonBase
              {...buttonProps}
              sx={{
                alignItems: "center",
                bgcolor: expanded ? colors.background : "transparent",
                border: expanded ? `1px solid ${colors.border}` : "1px solid transparent",
                borderRadius: rail ? "50%" : 2,
                color: colors.muted,
                display: "flex",
                gap: showLabel ? 1.5 : 0,
                height: rail ? 48 : 56,
                justifyContent: "flex-start",
                overflow: "hidden",
                pl: rail ? "10px" : 1,
                pr: expanded ? 0.75 : 0,
                transition: [
                  SIDEBAR_COLOR_TRANSITION,
                  "border-radius 320ms cubic-bezier(0.4, 0, 0.2, 1)",
                  "gap 260ms cubic-bezier(0.4, 0, 0.2, 1)",
                  "height 320ms cubic-bezier(0.4, 0, 0.2, 1)",
                  "padding 320ms cubic-bezier(0.4, 0, 0.2, 1)",
                  "width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
                ].join(", "),
                width: mobile ? "100%" : rail ? 48 : APP_SHELL.sidebarExpandedWidth - 48,
                "@media (max-height: 760px)": {
                  height: rail ? 44 : 50,
                  pl: rail ? "8px" : 1,
                  width: mobile ? "100%" : rail ? 44 : APP_SHELL.sidebarExpandedWidth - 48,
                },
                "&:hover": {
                  bgcolor: expanded ? colors.background : colors.activeBg,
                },
                "&:focus-visible": {
                  outline: (theme) => `2px solid ${theme.palette.border.focus}`,
                  outlineOffset: 2,
                },
              }}
            >
              {avatar}
              <Box
                sx={{
                  flex: 1,
                  maxWidth: showLabel ? 132 : 0,
                  minWidth: 0,
                  opacity: showLabel ? 1 : 0,
                  overflow: "hidden",
                  transition: "opacity 140ms ease, max-width 260ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    transition: "color 180ms ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    color: colors.muted,
                    fontSize: 11,
                    lineHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    transition: "color 180ms ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {meta}
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexShrink: 0,
                  justifyContent: "center",
                  opacity: showLabel ? 1 : 0,
                  overflow: "hidden",
                  transition: "opacity 140ms ease, width 260ms cubic-bezier(0.4, 0, 0.2, 1)",
                  width: showLabel ? 30 : 0,
                }}
              >
                <More2Line aria-hidden size={18} color="currentColor" />
              </Box>
            </ButtonBase>
          );

          return (
            <Tooltip
              title={rail ? "Opções da conta" : ""}
              placement="right"
              disableFocusListener={!rail}
              disableHoverListener={!rail}
              disableTouchListener={!rail}
            >
              {trigger}
            </Tooltip>
          );
        }}
      />
      <UserProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onSaveProfile={handleProfileSave}
        profileSaving={profileSaving}
        user={user}
      />
    </Box>
  );
}

function DrawerActions({ colors, onClose, onToggleDarkMode, sidebarDark }) {
  function openDocumentation() {
    window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
    onClose?.();
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: "0 0 auto",
        px: { xs: 2, sm: 3 },
        pt: 1,
        "@media (max-height: 760px)": {
          pt: 0.5,
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: colors.navText,
            fontSize: 11,
            fontWeight: 700,
            mb: 1,
            transition: "color 180ms ease",
          }}
        >
          Ações
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <ButtonBase
            type="button"
            onClick={openDocumentation}
            sx={{
              alignItems: "center",
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              color: colors.muted,
              display: "flex",
              gap: 1,
              height: 48,
              justifyContent: "flex-start",
              px: 2,
              minWidth: 0,
              transition: SIDEBAR_COLOR_TRANSITION,
              width: "100%",
              "@media (max-height: 760px)": {
                height: 42,
              },
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Documentação</Typography>
          </ButtonBase>
          {SHOW_SHELL_NOTIFICATIONS ? (
            <ButtonBase
              type="button"
              sx={{
                alignItems: "center",
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                color: colors.muted,
                display: "flex",
                gap: 1,
                height: 48,
                justifyContent: "flex-start",
                px: 2,
                minWidth: 0,
                transition: SIDEBAR_COLOR_TRANSITION,
                width: "100%",
                "@media (max-height: 760px)": {
                  height: 42,
                },
              }}
            >
              <NotificationsNoneRoundedIcon
                sx={{ color: colors.activeText, fontSize: 18, transition: "color 180ms ease" }}
              />
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Notificações</Typography>
            </ButtonBase>
          ) : null}
        </Stack>
      </Box>

      <Box>
        <Divider sx={{ borderColor: colors.border, mb: 2, transition: "border-color 180ms ease" }} />
        <Typography
          sx={{
            color: colors.navText,
            fontSize: 11,
            fontWeight: 700,
            mb: 1,
            transition: "color 180ms ease",
          }}
        >
          Preferências
        </Typography>
        <ButtonBase
          type="button"
          onClick={onToggleDarkMode}
          sx={{
            alignItems: "center",
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            color: colors.muted,
            display: "flex",
            height: 44,
            justifyContent: "flex-start",
            px: 2,
            transition: SIDEBAR_COLOR_TRANSITION,
            width: "100%",
            "@media (max-height: 760px)": {
              height: 40,
            },
          }}
        >
          <DarkModeOutlinedIcon sx={{ fontSize: 16, mr: 1.5 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Tema</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, ml: "auto" }}>
            {sidebarDark ? "Dark" : "Light"}
          </Typography>
        </ButtonBase>
      </Box>
    </Stack>
  );
}

export default function MenuContent({
  compact = false,
  labelVisible = !compact,
  mobile = false,
  onItemClick,
  onToggleDarkMode,
  shellCompact = compact,
  sidebarDark = false,
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = getPrivateShellColors(theme, sidebarDark);

  return (
    <Box
      sx={{
        "--app-shell-active-bg": colors.activeBg,
        "--app-shell-active-text": colors.activeText,
        "--app-shell-bg": colors.background,
        "--app-shell-border": colors.border,
        "--app-shell-brand": colors.brand,
        "--app-shell-muted": colors.muted,
        "--app-shell-surface": colors.surface,
        "--app-shell-text": colors.text,
        bgcolor: colors.surface,
        borderRight: `1px solid ${colors.border}`,
        boxSizing: "border-box",
        color: colors.text,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        transition: SIDEBAR_COLOR_TRANSITION,
        width: "100%",
      }}
    >
      <Navigation
        compact={compact && !mobile}
        colors={colors}
        labelVisible={labelVisible || mobile}
        mobile={mobile}
        onItemClick={onItemClick}
      />
      {mobile ? (
        <DrawerActions
          colors={colors}
          onClose={onItemClick}
          onToggleDarkMode={onToggleDarkMode}
          sidebarDark={sidebarDark}
        />
      ) : null}
      <Box
        sx={{
          minHeight: 0,
          mt: mobile ? 2 : "auto",
        }}
      >
        <UserFooter
          compact={shellCompact && !mobile}
          colors={colors}
          labelVisible={labelVisible || mobile}
          mobile={mobile}
          onLogout={async () => {
            await logout();
            onItemClick?.();
          }}
          onSwitchAccount={() => {
            navigate("/selecionar-conta?modo=trocar", {
              state: { mode: "switch-account" },
            });
            onItemClick?.();
          }}
        />
      </Box>
    </Box>
  );
}
